/**
 * Formats odds in various formats.
 *
 * @module app/components/oddsFormatProvider
 */
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import PreferencesProvider from './PreferencesProvider';
import Translator from './Translator';
import { get } from '../../utils';
import DeviceDetector from './DeviceDetector';

const EVS = 'EVS';

/**
 * Current page Object
 *
 * @type    {Object}
 */
var currentPage = null;

/**
 * Odd Format value cached to avoid multiples call to local storage
 *
 * @type    {String}
 */
var oddsFormatCache = null;

/**
 * Price display cached value from Open Bet
 *
 * @type    {String}
 */
var openbetPriceDisplayValue = null;

/**
 * This function invalid the cache of the oddformat OB and local storage access
 *
 * @returns     {void}
 *
 * @private
 */
var invalidateOddsFormatCache = function () {

    openbetPriceDisplayValue = null;
    oddsFormatCache = null;
};

/**
 * Returns language specific default format or cached value
 *
 * @returns     {String}
 *
 * @private
 */
var getDefaultPriceDisplayValue = function () {

    if (!openbetPriceDisplayValue) {

        return getDefaultPriceDisplayValueNoCache();
    } else {

        return openbetPriceDisplayValue;
    }
};

/**
 * Returns price_display value calculated based on current language
 *
 * @returns     {String}
 *
 * @private
 */
var getDefaultPriceDisplayValueNoCache = function () {

    return getOpenBetDefaultPriceDisplay(Translator.getLocale());
};

/**
 * Returns the cached or default odds-format
 *
 * @returns {string} 'ODDS' | 'DECIMAL' | 'AMERICAN'
 *
 * @private
 */
var getDefaultPriceDisplay = function () {

    openbetPriceDisplayValue = getDefaultPriceDisplayValue();

    return openbetPriceDisplayValue;
};

/**
 * Get default openbet format based on locale
 *
 * @param {String} locale
 *
 * @returns {String}
 *
 * @private
 */
var getOpenBetDefaultPriceDisplay = function (locale) {

    var formats = {
        'en-gb': 'ODDS',
        'en-us': 'AMERICAN',
        'other': 'DECIMAL',
    };

    return formats[locale] || formats.other;
};

/**
 * Get openbet's name for an odds format.
 *
 * @param {String} myFormat
 *
 * @returns {String}
 *
 * @private
 */
var getOpenBetFormatsKey = function (myFormat) {

    var formats = {
        'fraction': 'ODDS',
        'decimal': 'DECIMAL',
        'american': 'AMERICAN',
    };

    return formats[myFormat];
};

/**
 * Given the openbet format, convert it into 'our' format.
 * Why do we need this conversion??
 *
 * @param {String} format
 *
 * @returns {String}
 *
 * @private
 */
var getReverseFormatKey = function (format) {

    var formats = {
        'ODDS': 'fraction',
        'DECIMAL': 'decimal',
        'AMERICAN': 'american',
    };

    return formats[format];
};

var getCustomerPref = function () {

    if (document.sportsbookAPI && typeof document.sportsbookAPI.getCustomerPref === 'function') {
        return document.sportsbookAPI.getCustomerPref('price_display');
    }
};

/**
 * Sets the odds-format on the OB cookie
 *
 * @param priceDisplay {string} 'fraction' | 'decimal' | 'american'
 *
 * @returns {boolean} successfully set the cookie parameter
 *
 * @private
 */
var saveOpenbetPriceDisplay = function (priceDisplay) {

    priceDisplay = getOpenBetFormatsKey(priceDisplay);

    if (typeof document.sportsbookAPI !== 'undefined') {
        document.sportsbookAPI.setCustomerPref('price_display', priceDisplay);
        return true;
    }

    return false;
};

/**
 * Functions for save and getting price format for openbet (thus the betslip too).
 *
 * @returns {{save: bool, get: string}}
 *
 * @private
 */
var openBetFormat = function () {

    return {
        save: saveOpenbetPriceDisplay,
        get: getDefaultPriceDisplay,
    };
};

/**
 * Apply user preferences based on OB cookie to page
 *
 * @returns {void}
 *
 * @private
 */
var applyUserPreferences = function () {

    var userPreference = getReverseFormatKey(getCustomerPref());

    if (userPreference && userPreference !== oddsFormatCache) {

        oddsFormatCache = userPreference;

        PreferencesProvider.setPreference(
            'oddsFormat',
            oddsFormatCache
        );

        api.oddsConvert(true);

        notifyOddsFormatChanged(oddsFormatCache);
    }
};

/**
 * Set the user preference to default
 *
 * @returns {void}
 *
 * @private
 */
var setInitialOddsPreference = function () {

    const format = PreferencesProvider.getPreference('oddsFormat') ||
        get(window, 'whStatic.config.defaultOddsFormat') ||
        getReverseFormatKey(getDefaultPriceDisplay());

    PreferencesProvider.setPreference('oddsFormat', format);

    oddsFormatCache = format;
};

/**
 * Certain odds should not follow the standard rounding when converting to decimal, they are:
 *
 * @type {Object}
 */
var decimalRoundingExceptions = {
    '13/8': 2.62,
    '15/8': 2.87,
    '11/8': 2.37,
    '8/13': 1.61,
    '2/7': 1.28,
    '1/8': 1.12,
    '123/200': 1.62,
};

/**
 * Convert odds to
 *
 * @type {Object}
 */
var convertOddsTo = {

    /**
     * turns odds into decimal format
     *
     * @param {Integer} num
     * @param {Integer} denom
     *
     * @returns {Number}
     */
    decimal: function (num, denom) {
        var result = (num / denom) + 1;

        result = (
            typeof decimalRoundingExceptions[num + '/' + denom] === 'undefined'
        ) ? result.toFixed(2) : decimalRoundingExceptions[num + '/' + denom];

        return result;
    },

    /**
     * turns odds in american format
     *
     * @param {Integer} num
     * @param {Integer} denom
     *
     * @returns {string}
     */
    american: function (num, denom) {

        var result;

        if (num >= denom) {
            result = 100 * (num / denom);
            return '+' + result.toFixed(0);
        } else {
            result = -100 / (num / denom);
            return result.toFixed(0);
        }
    },

    /**
     * turns odds into fraction format
     *
     * @param {Integer} num
     * @param {Integer} denom
     *
     * @returns {string}
     */
    fraction: function (num, denom) {

        return (num === 1 && denom === 1) ? EVS : num + '/' + denom;
    },
};

/**
 * method to convert the format of odds (based on the one in version 1)
 *
 * @param {String} format
 * @param {Integer} num
 * @param {Integer} denom
 * @param {Boolean} useDecimalNumerator Should we parse the numerator as a float
 *
 * @returns {String/Number}
 *
 * @private
 */
var oddsFormatConversion = function (format, num, denom, useDecimalNumerator) {

    /*The num/denom have been getting passed as string which breaks
    the comparison, convert to int first*/
    num = useDecimalNumerator ? parseFloat(num, 10) : parseInt(num, 10);
    denom = parseInt(denom, 10);

    //convert odds to right format
    return convertOddsTo[format.toLowerCase()](num, denom);
};

/**
 * methods for the select dropdown
 *
 * @returns {void}
 *
 * @private
 */
var select = {

    $filter: null,

    $buttons: null,

    $toggle: null,

    /**
     * Changes format states for button
     *
     * @param   {Object} el             Element object
     * @param   {String} selectedFormat Format selected
     *
     * @returns {void}
     *
     * @private
     */
    changeButtonFormat: function ($el, selectedFormat) {

        selectedFormat = selectedFormat || $el.data('oddsFormat');
        select.$buttons.removeClass('-active');
        $el.addClass('-active');
        select.$toggle.attr('data-selected-format', selectedFormat);
    },

    /**
     * Changes format states
     *
     * @param   {String} selectedFormat Format selected
     *
     * @returns {void}
     *
     * @private
     */
    changeFormat: function (selectedFormat) {
        if (select.$buttons) {
            var $el = select.$buttons.find('[data-odds-format="' + selectedFormat + '"]');
            select.changeButtonFormat($el, selectedFormat);
        }
    },

    /**
     * Force format change
     *
     * @param   {Object} self
     * @param   {String} selOddsFormat
     *
     * @returns {void}
     *
     * @private
     */
    reallyChangeFormat: function (selOddsFormat) {

        select.changeFormat(selOddsFormat);
        PreferencesProvider.setPreference('oddsFormat', selOddsFormat);
        openBetFormat().save(selOddsFormat);
        api.oddsConvert(true);

        window.WH.messageBus.publishSticky(MessageBusTopics.BETSLIP_PRICE_FORMAT_SET, selOddsFormat);
    },

    /**
     * Action to change odds format
     *
     * @param   {Object} event
     *
     * @returns {void}
     *
     * @private
     */
    changeFormatAction: function (event) {

        var $self = jQuery(this);
        var selOddsFormat = $self.data('oddsFormat');
        select.format = selOddsFormat;
        invalidateOddsFormatCache();

        if (selOddsFormat !== PreferencesProvider.getPreference('oddsFormat')) {

            notifyOddsFormatChanged(selOddsFormat);
            select.reallyChangeFormat(selOddsFormat);
        }

        event.preventDefault();
    },

    /**
     * Action to change odds format
     *
     * @param   {string} newOddsFormat new odds format
     *
     * @returns {void}
     *
     * @private
     */
    changeFormatInternalAction: function (newOddsFormat) {

        select.format = newOddsFormat;
        invalidateOddsFormatCache();

        if (newOddsFormat !== PreferencesProvider.getPreference('oddsFormat')) {

            select.reallyChangeFormat(newOddsFormat);
        }

    },
};

/**
 * method to get the selected format using a cache
 *
 * @returns {String}
 *
 * @private
 */
var getFormat = function () {

    if (oddsFormatCache) {

        return oddsFormatCache;
    }

    return getFormatNoCached();
};

/**
 * method to get the selected format (fraction by default)
 *
 * @returns {String}
 *
 * @private
 */
var getFormatNoCached = function () {

    var format = 'fraction';

    if (PreferencesProvider.getPreference('oddsFormat')) {

        format = PreferencesProvider.getPreference('oddsFormat');
    }

    format = getReverseFormatKey(getOpenBetFormatsKey(format));

    oddsFormatCache = format;

    return format;
};

/**
 * method to do an ajax call to refresh the betslip
 *
 * @param {Boolean} reloadBetSlip
 *
 * @returns {void}
 *
 * @private
 */
var refreshOdds = function (reloadBetSlip) {

    jQuery('body').find('button.oddsbutton:not([data-odds=SP])').each(changeOddFormatInSelection);
    if (!api.isInternational()) {
        jQuery('body').find('p.btmarket__was-price').each(changeOddFormatInWasPrice);
    }
};

/**
 * Change odd format in the previous odds elements
 *
 * @param   element DOM Element containing a previous odd
 *
 * @returns {void}
 *
 * @private
 */
var updatePreviousOddsFormat = function updatePreviousOddsFormat(element) {

    var currentHTML = element.html();

    if (currentHTML && /\d+\/\d+/g.test(currentHTML)) {

        element.html(api.convertSingle(element.html()));
    }

};
/**
 * Change odd format in selection
 *
 * @returns {void}
 *
 * @private
 */
var changeOddFormatInSelection = function () {

    var $element = jQuery(this);

    var odds = {
        num: +$element.get(0).getAttribute('data-num'),
        denom: +$element.get(0).getAttribute('data-denom'),
    };

    var outcomeId = $element.get(0).getAttribute('id');

    if (currentPage && !currentPage.skipPreviousOddsFormatUpdate && !/\|/.test(outcomeId)) {

        updatePreviousOddsFormat(jQuery('#previous_odd_item_' + outcomeId));
    }

    odds.oddsToDisplay = oddsFormatConversion(getFormat(), odds.num, odds.denom);

    validateAndUpdateOdds(this, odds);

};

/**
 * Change odd format in was price
 *
 * @returns {void}
 *
 * @private
 */
var changeOddFormatInWasPrice = function () {

    var $element = jQuery(this);

    var odds = {
        num: +$element.get(0).getElementsByClassName('was-price')[0].getAttribute('data-num'),
        denom: +$element.get(0).getElementsByClassName('was-price')[0].getAttribute('data-denom'),
    };

    odds.oddsToDisplay = oddsFormatConversion(getFormat(), odds.num, odds.denom);

    validateAndUpdateOdds(this, odds);
};

/**
 * method to validate and update odds format
 *
 * @param {Object} el
 * @param {Object} odds
 *
 * @returns {void}
 *
 * @private
 */
var validateAndUpdateOdds = function (el, odds) {

    /**
     * method to update the odds format
     *
     * @param {object} el
     *
     * @returns {void}
     *
     * @private
     */
    var updateSelectionOddFormat = function (el) {

        var $el = jQuery(el);

        $el.attr('data-odds', odds.oddsToDisplay);

        $el.children('.betbutton__odds').text(odds.oddsToDisplay);

        $el.children('.selectiondetails').attr('data-odds', odds.oddsToDisplay);

    };

    /**
     * method to update the odds format
     *
     * @param {object} el
     *
     * @returns {void}
     *
     * @private
     */
    var updateWasPriceOddFormat = function (el) {

        if (!jQuery(el).hasClass('oddsbutton') && !!jQuery(el).get(0).getElementsByClassName('was-price').length) {
            var $el = jQuery(el);
            $el.children('.was-price').text(odds.oddsToDisplay);
        }
    };

    /**
     * method to update the odds format if it is within topbets (as format is different)
     *
     * @param {object} el
     *
     * @returns {void}
     *
     * @private
     */
    var updateTopBetsSelectionOddFormat = function (el) {

        if (!jQuery(el).hasClass('oddsbutton') && !jQuery(el).get(0).getElementsByClassName('was-price').length) {
            jQuery(el).html('<span class="ng-binding">' + odds.oddsToDisplay + '</span>');
        }
    };

    if (odds.num === parseInt(odds.num, 10) &&
        odds.denom === parseInt(odds.denom, 10)) {
        updateSelectionOddFormat(el);
        updateTopBetsSelectionOddFormat(el);
        if(!api.isInternational()) {
            updateWasPriceOddFormat(el);
        }
    }
};

/**
 * Notifies the change of odds format through the message bus
 *
 * @param {string} newFormat The new odd format set to be notified
 *
 * @returns {void}
 *
 * @private
 */
var notifyOddsFormatChanged = function notifyOddsFormatChanged(newFormat) {

    window.WH.messageBus.publishSticky(MessageBusTopics.ON_ODDS_FORMAT_CHANGED, newFormat);
};

/**
 * Public functions.
 *
 * @type {Object}
 */
var api = {

    isInternational: function () {
        return DeviceDetector.isInternational();
    },

    select: function () {

        select.$filter = jQuery('.odds-format-filter');
        select.$buttons = select.$filter.find('.odds-format__button');
        select.$toggle = select.$filter.find('.odds-format__toggle');

        if (select.$filter.length) {

            select.$buttons.on('click', select.changeFormatAction);
        }
    },

    /**
     * method to convert all the odd on the page into the specified format
     *
     * @param reloadBetSlip {Boolean}
     *
     * @returns {void}
     *
     * @public
     */
    oddsConvert: function (reloadBetSlip) {

        refreshOdds(reloadBetSlip);

        //check that the dropdown has the correct selected option
        select.changeFormat(oddsFormatCache);
    },

    /**
     * Convert a single price into the user's format.
     *
     * @param {String} price
     * @param {Boolean} useDecimalNumerator Should we parse the numerator as a float
     *
     * @returns {String}
     *
     * @public
     */
    convertSingle: function (price, useDecimalNumerator) {

        var numDenum = price === EVS ? [1, 1] : price.split('/');

        return oddsFormatConversion(
            //Removed call to getReverseFormatKey(getDefaultPriceDisplay()) to fix SPD-1721
            getFormat(),
            numDenum[0],
            numDenum[1],
            useDecimalNumerator
        );
    },

    /**
     * Initialisation.
     *
     * @returns {void}
     *
     * @public
     */
    init: function () {
        //initialise the select functionality
        api.select();

        window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_INIT_COMPLETE, function () {
            applyUserPreferences();
        });

        setInitialOddsPreference();

        window.WH.messageBus.subscribe(MessageBusTopics.CURRENT_PAGE, function (topic, page) {
            currentPage = page;
        });

        window.WH.messageBus.subscribe(MessageBusTopics.ON_ODDS_FORMAT_CHANGED, function (topic, format) {
            select.changeFormatInternalAction(format);
        });

        //do initial convert of odds
        api.oddsConvert(false);

        // do initial notification of odds format change
        notifyOddsFormatChanged(getFormat());

    },

    /**
     * Updates only selected fragment of odds
     *
     * @param {Object} element Element in which to search for odds buttons
     *
     * @returns {void}
     *
     * @private
     */
    updateFragmentOdds: function (element) {

        jQuery(element).find('button.oddsbutton:not([data-odds=SP])').each(changeOddFormatInSelection);
    },
};


export default api;
